import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, API, formatApiError } from "../lib/api";
import {
  Lock, Tooth, FilePdf, Image as ImageIcon, ArrowRight, Pulse, Phone, EnvelopeSimple,
  WhatsappLogo, Sparkle, Warning,
} from "@phosphor-icons/react";
import ErrorBoundary from "../components/ErrorBoundary";
import PortalAssistant from "../components/PortalAssistant";

function PortalInner() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const followupIdFromUrl = searchParams.get("followup_id") || "";

  const [info, setInfo] = useState(null);
  const [infoLoading, setInfoLoading] = useState(true);
  const [infoError, setInfoError] = useState("");

  const [clinic, setClinic] = useState({});
  const [pin, setPin] = useState("");
  const [pinError, setPinError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [patient, setPatient] = useState(null);
  const [patientToken, setPatientToken] = useState("");
  const [files, setFiles] = useState([]);
  const [journey, setJourney] = useState(null);
  const [followupMessage, setFollowupMessage] = useState("");

  // No-token landing — still fetch clinic so the header is branded
  useEffect(() => {
    if (!token) {
      api.get(`/settings/public`).then((r) => setClinic(r?.data || {})).catch(() => {}).finally(() => setInfoLoading(false));
      return;
    }
    (async () => {
      setInfoLoading(true);
      try {
        const [infoRes, clinicRes] = await Promise.all([
          api.get(`/portal/${token}/info`),
          api.get(`/settings/public`).catch(() => ({ data: {} })),
        ]);
        setInfo(infoRes.data || null);
        setClinic(clinicRes?.data || {});
      } catch (e) {
        setInfoError(formatApiError(e?.response?.data?.detail) || "Link non valido o scaduto. Contatta Mediadent Sarzana.");
      } finally {
        setInfoLoading(false);
      }
    })();
  }, [token]);

  const submitPin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setPinError("");
    try {
      const verifyRes = await api.post(`/portal/${token}/verify`, { pin });
      const data = verifyRes?.data || {};
      const patientObj = data.patient || null;
      const ptoken = data.patient_token || "";
      if (!patientObj || !ptoken) throw new Error("Risposta inattesa dal server");
      setPatient(patientObj);
      setPatientToken(ptoken);

      // Fetch files & journey in parallel, but don't break the page if either fails.
      const headers = { Authorization: `Bearer ${ptoken}` };
      const [filesRes, journeyRes] = await Promise.allSettled([
        api.get(`/portal/files`, { headers }),
        api.get(`/portal/journey`, { headers }),
      ]);
      if (filesRes.status === "fulfilled") {
        const list = Array.isArray(filesRes.value?.data) ? filesRes.value.data : [];
        setFiles(list.map((f) => ({ ...(f || {}), _token: ptoken })));
      }
      if (journeyRes.status === "fulfilled") {
        const j = journeyRes.value?.data || null;
        setJourney(j);
        const lastFu = (j?.timeline || []).filter((t) => t?.kind === "followup").slice(-1)[0];
        if (lastFu?.message) setFollowupMessage(String(lastFu.message));
      }
    } catch (err) {
      setPinError(formatApiError(err?.response?.data?.detail) || "PIN non valido");
    } finally {
      setSubmitting(false);
    }
  };

  // Computed contact info (clinic)
  const clinicPhone = (clinic?.phone || "").trim();
  const clinicEmail = (clinic?.email || "").trim();
  const clinicWhatsapp = clinicPhone ? clinicPhone.replace(/\D/g, "") : "";
  const logoUrl = clinic?.logo_path ? `${API}/settings/logo` : null;

  // ---- RENDERS ----

  // 1) No token → landing
  if (!token) {
    return (
      <Shell clinic={clinic} logoUrl={logoUrl}>
        <Card>
          <div className="text-xs uppercase tracking-[0.25em] text-brand-400 mb-2">Portale paziente</div>
          <h1 className="font-heading text-2xl font-medium mb-3">Apri il link che hai ricevuto</h1>
          <p className="text-[#56635D] text-sm">Per accedere al portale paziente devi usare il link personale ricevuto via WhatsApp dallo studio.</p>
          <button onClick={() => navigate("/login")} className="mt-6 text-sm text-brand-700 underline">Vai al login operatori</button>
        </Card>
      </Shell>
    );
  }

  // 2) Loading initial info
  if (infoLoading) {
    return (
      <Shell clinic={clinic} logoUrl={logoUrl}>
        <Card>
          <div className="text-center text-[#56635D] py-6" data-testid="portal-loading">
            <Pulse size={22} weight="duotone" className="text-brand-700 mx-auto mb-3" />
            Caricamento del portale…
          </div>
        </Card>
      </Shell>
    );
  }

  // 3) Invalid/expired token
  if (infoError || !info) {
    return (
      <Shell clinic={clinic} logoUrl={logoUrl}>
        <Card>
          <div className="text-center" data-testid="portal-invalid">
            <div className="w-12 h-12 rounded-full bg-[#A84A4A]/10 mx-auto mb-3 flex items-center justify-center">
              <Warning size={22} weight="duotone" className="text-[#A84A4A]" />
            </div>
            <h1 className="font-heading text-2xl font-medium">Link non valido o scaduto</h1>
            <p className="text-[#56635D] mt-2">Contatta {clinic?.clinic_name || "Mediadent Sarzana"} per ricevere un nuovo link.</p>
            <ContactRow phone={clinicPhone} email={clinicEmail} whatsapp={clinicWhatsapp} />
          </div>
        </Card>
      </Shell>
    );
  }

  // 4) PIN gate
  if (!patient) {
    return (
      <Shell clinic={clinic} logoUrl={logoUrl}>
        <Card testId="portal-pin-card">
          <div className="text-xs uppercase tracking-[0.25em] text-brand-400">Benvenuto/a</div>
          <h1 className="font-heading text-3xl font-medium tracking-tight mt-2">{info.first_name} {info.last_name}</h1>
          <p className="text-[#56635D] mt-3 flex items-start gap-2">
            <Lock size={16} weight="duotone" className="mt-0.5 text-brand-400 shrink-0" />
            Per sicurezza inserisci il PIN che hai ricevuto dallo studio.
          </p>
          <form onSubmit={submitPin} className="mt-8 space-y-4 max-w-sm">
            <input
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              placeholder="••••••"
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              data-testid="portal-pin-input"
              className="pin-input w-full px-4 py-4 border border-[#E6E8E6] rounded-lg bg-white focus:border-brand-700"
            />
            {pinError && <div data-testid="portal-error" className="text-sm text-[#A84A4A] bg-[#A84A4A]/10 rounded-lg px-3 py-2">{pinError}</div>}
            <button type="submit" disabled={submitting || pin.length < 4} data-testid="portal-submit-button" className="w-full inline-flex items-center justify-center gap-2 bg-brand-700 hover:bg-brand-800 text-white px-5 py-3 rounded-lg disabled:opacity-60">
              {submitting ? "Verifica…" : (<>Accedi <ArrowRight size={16} weight="bold" /></>)}
            </button>
          </form>
          <ContactRow phone={clinicPhone} email={clinicEmail} whatsapp={clinicWhatsapp} />
        </Card>
      </Shell>
    );
  }

  // 5) Patient view
  const implants = Array.isArray(patient.implants) ? patient.implants : [];
  const medications = Array.isArray(patient.medications) ? patient.medications : [];
  const interventionLine = `${patient.intervention_type || ""}${patient.intervention_date ? ` (${patient.intervention_date})` : ""}\n${patient.intervention_details || ""}`.trim();

  return (
    <Shell clinic={clinic} logoUrl={logoUrl}>
      <div className="space-y-6 fade-up" data-testid="portal-patient-view">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-brand-400">Il tuo referto</div>
          <h1 className="font-heading text-3xl font-medium tracking-tight mt-2" data-testid="portal-patient-name">
            {patient.first_name} {patient.last_name}
          </h1>
        </div>

        <ContactCard
          clinic={clinic}
          phone={clinicPhone}
          email={clinicEmail}
          whatsapp={clinicWhatsapp}
        />

        {/* AI Assistant — Section ABOVE everything */}
        <PortalAssistant patientFirstName={patient.first_name} patientToken={patientToken} followupId={followupIdFromUrl} />

        <Block label="Intervento" body={interventionLine} testId="portal-intervention" />
        <Block label="Istruzioni post-operatorie" body={patient.post_op_instructions} testId="portal-post-op" />
        <Block label="Materiali utilizzati" body={patient.materials_used} testId="portal-materials" />

        {/* Follow-up AI message (sanitized — only the patient-facing message) */}
        {followupMessage && (
          <div className="elev-card p-6" data-testid="portal-followup-ai">
            <div className="text-xs uppercase tracking-[0.2em] text-brand-400 mb-2 flex items-center gap-2">
              <Sparkle size={14} weight="duotone" /> Messaggio dallo studio
            </div>
            <p className="text-[#1C2321] whitespace-pre-line leading-relaxed">{followupMessage}</p>
          </div>
        )}

        {/* Il tuo percorso di cura */}
        {journey && ((journey.timeline || []).length > 0 || journey.next_step) && (
          <div className="elev-card p-6" data-testid="portal-journey">
            <div className="text-xs uppercase tracking-[0.2em] text-brand-400 mb-2">Il tuo percorso di cura</div>
            <h3 className="font-heading text-xl font-medium text-[#1C2321] mb-1">Come procede</h3>
            {journey.next_step && (
              <div className="mt-3 p-3 rounded-lg bg-brand-50 border border-brand-200 flex items-start gap-2">
                <Pulse size={18} weight="duotone" className="text-brand-700 mt-0.5 shrink-0" />
                <div className="text-sm text-[#1C2321]"><strong className="font-medium">Prossimo passo:</strong> {journey.next_step}</div>
              </div>
            )}
            {(journey.timeline || []).length > 0 && (
              <ol className="relative border-l-2 border-brand-100 ml-2 space-y-4 mt-5">
                {journey.timeline.map((t, i) => (
                  <li key={i} className="pl-5 relative">
                    <span className="absolute -left-[7px] top-1.5 w-3 h-3 rounded-full bg-brand-400 border-2 border-white" />
                    <div className="flex flex-wrap items-baseline gap-2">
                      <span className="font-medium text-[#1C2321]">
                        {t?.kind === "followup" ? `Controllo giorno ${t?.day ?? ""}` : `Documento: ${t?.filename || ""}`}
                      </span>
                      <span className="text-xs font-mono text-[#56635D]">{t?.date || ""}</span>
                    </div>
                    {t?.kind === "followup" && t?.status && (
                      <div className="text-sm text-[#56635D] mt-0.5">Stato: {t.status}</div>
                    )}
                  </li>
                ))}
              </ol>
            )}
          </div>
        )}

        {implants.length > 0 && (
          <div className="elev-card p-6" data-testid="portal-implants">
            <div className="text-xs uppercase tracking-[0.2em] text-brand-400 mb-3">Impianti</div>
            <ul className="space-y-2">
              {implants.map((im, i) => (
                <li key={i} className="text-sm text-[#1C2321]">
                  <span className="font-medium">{im?.brand || "-"} {im?.model || ""}</span>
                  <span className="text-[#56635D]"> • Ø{im?.diameter || "-"} × L{im?.length || "-"} • Lotto {im?.lot || "-"}{im?.position ? ` • ${im.position}` : ""}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {medications.length > 0 && (
          <div className="elev-card p-6" data-testid="portal-medications">
            <div className="text-xs uppercase tracking-[0.2em] text-brand-400 mb-3">Farmaci</div>
            <ul className="space-y-3">
              {medications.map((m, i) => (
                <li key={i}>
                  <div className="text-sm font-medium text-[#1C2321]">{m?.name || ""}</div>
                  <div className="text-xs text-[#56635D]">{m?.dosage || ""} {m?.frequency || ""} {m?.duration ? `per ${m.duration}` : ""}</div>
                  {m?.notes && <div className="text-xs text-[#56635D] mt-1">{m.notes}</div>}
                </li>
              ))}
            </ul>
          </div>
        )}

        {files.length > 0 && (
          <div className="elev-card p-6" data-testid="portal-files">
            <div className="text-xs uppercase tracking-[0.2em] text-brand-400 mb-3">Documenti & immagini</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {files.map((f) => {
                if (!f?.id) return null;
                const isImg = String(f.content_type || "").startsWith("image/");
                const url = `${API}/files/${f.id}/download?portal=${encodeURIComponent(f._token || "")}`;
                return (
                  <a key={f.id} href={url} target="_blank" rel="noreferrer" className="elev-card p-4 flex items-center gap-3 hover:-translate-y-[1px]" data-testid={`portal-file-${f.id}`}>
                    <div className="w-10 h-10 rounded-lg bg-brand-50 flex items-center justify-center shrink-0">
                      {isImg ? <ImageIcon size={18} weight="duotone" className="text-brand-700" /> : <FilePdf size={18} weight="duotone" className="text-brand-700" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{f.original_filename || "file"}</div>
                      <div className="text-xs text-[#56635D]">{Math.round((f.size || 0) / 1024)} KB</div>
                    </div>
                  </a>
                );
              })}
            </div>
          </div>
        )}

        <div className="text-xs text-[#56635D] text-center pt-4">
          {clinic?.clinic_name || "Mediadent Sarzana"} • care.mediadentsarzana.it
        </div>
      </div>
    </Shell>
  );
}

// --- Sub-components ---

function Shell({ children, clinic, logoUrl }) {
  return (
    <div className="min-h-screen bg-paper">
      <header className="glass sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center gap-3">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt="Mediadent"
              className="w-9 h-9 rounded-md object-contain bg-white border border-[#E6E8E6]"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
              data-testid="portal-clinic-logo"
            />
          ) : (
            <div className="w-9 h-9 rounded-md bg-brand-700 text-white flex items-center justify-center">
              <Tooth size={18} weight="duotone" />
            </div>
          )}
          <div className="min-w-0">
            <div className="font-heading font-semibold leading-none text-[#1C2321] truncate">{clinic?.clinic_name || "Mediadent Sarzana"}</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-brand-400 mt-1">Portale paziente</div>
          </div>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 py-10">
        {children}
      </main>
    </div>
  );
}

function Card({ children, testId }) {
  return (
    <div className="elev-card p-8 fade-up" data-testid={testId}>
      {children}
    </div>
  );
}

function Block({ label, body, testId }) {
  if (!body || !String(body).trim()) return null;
  return (
    <div className="elev-card p-6" data-testid={testId}>
      <div className="text-xs uppercase tracking-[0.2em] text-brand-400 mb-3">{label}</div>
      <p className="text-[#1C2321] whitespace-pre-line leading-relaxed">{body}</p>
    </div>
  );
}

function ContactRow({ phone, email, whatsapp }) {
  if (!phone && !email && !whatsapp) return null;
  return (
    <div className="mt-6 flex flex-wrap justify-center gap-2" data-testid="portal-contact-row">
      {phone && (
        <a href={`tel:${phone}`} data-testid="portal-call" className="inline-flex items-center gap-2 bg-[#EAF0EC] text-brand-700 hover:bg-[#D5E2DB] px-3 py-2 rounded-lg text-sm">
          <Phone size={14} weight="duotone" /> Chiama
        </a>
      )}
      {whatsapp && (
        <a href={`https://wa.me/${whatsapp}`} target="_blank" rel="noreferrer" data-testid="portal-whatsapp" className="inline-flex items-center gap-2 bg-[#25D366] text-white hover:bg-[#1ebd5b] px-3 py-2 rounded-lg text-sm">
          <WhatsappLogo size={14} weight="fill" /> WhatsApp
        </a>
      )}
      {email && (
        <a href={`mailto:${email}`} data-testid="portal-email" className="inline-flex items-center gap-2 bg-[#EAF0EC] text-brand-700 hover:bg-[#D5E2DB] px-3 py-2 rounded-lg text-sm">
          <EnvelopeSimple size={14} weight="duotone" /> Email
        </a>
      )}
    </div>
  );
}

function ContactCard({ clinic, phone, email, whatsapp }) {
  if (!phone && !email && !whatsapp) return null;
  return (
    <div className="elev-card p-5" data-testid="portal-contact-card">
      <div className="text-xs uppercase tracking-[0.2em] text-brand-400 mb-3">Contatta lo studio</div>
      <div className="flex flex-wrap gap-2">
        {phone && (
          <a href={`tel:${phone}`} data-testid="portal-call-cta" className="inline-flex items-center gap-2 bg-brand-700 text-white hover:bg-brand-800 px-4 py-2.5 rounded-lg text-sm">
            <Phone size={16} weight="duotone" /> Chiama {clinic?.clinic_name || "lo studio"}
          </a>
        )}
        {whatsapp && (
          <a href={`https://wa.me/${whatsapp}`} target="_blank" rel="noreferrer" data-testid="portal-whatsapp-cta" className="inline-flex items-center gap-2 bg-[#25D366] text-white hover:bg-[#1ebd5b] px-4 py-2.5 rounded-lg text-sm">
            <WhatsappLogo size={16} weight="fill" /> WhatsApp
          </a>
        )}
        {email && (
          <a href={`mailto:${email}`} data-testid="portal-email-cta" className="inline-flex items-center gap-2 bg-[#EAF0EC] text-brand-700 hover:bg-[#D5E2DB] px-4 py-2.5 rounded-lg text-sm">
            <EnvelopeSimple size={16} weight="duotone" /> Email
          </a>
        )}
      </div>
    </div>
  );
}

// Top-level export wraps everything in ErrorBoundary so a runtime
// error never leaves the patient with a blank white screen.
export default function PatientPortal() {
  return (
    <ErrorBoundary
      fallback={(err, reset) => (
        <div className="min-h-screen bg-paper flex items-center justify-center p-6">
          <div className="elev-card p-8 max-w-md w-full text-center" data-testid="portal-error-boundary">
            <div className="w-12 h-12 rounded-full bg-[#A84A4A]/10 mx-auto mb-3 flex items-center justify-center">
              <Warning size={22} weight="duotone" className="text-[#A84A4A]" />
            </div>
            <h1 className="font-heading text-2xl font-medium">Si è verificato un errore</h1>
            <p className="text-[#56635D] mt-2">Ricarica la pagina o contatta Mediadent Sarzana per assistenza.</p>
            {err && err.message && (
              <details className="mt-4 text-left">
                <summary className="text-xs text-[#56635D] cursor-pointer">Dettagli tecnici</summary>
                <pre className="text-[10px] font-mono text-[#A84A4A] bg-[#A84A4A]/5 rounded p-2 mt-2 overflow-auto">{String(err.message).slice(0, 400)}</pre>
              </details>
            )}
            <button onClick={reset} className="mt-6 bg-brand-700 hover:bg-brand-800 text-white px-5 py-2.5 rounded-lg text-sm">Ricarica pagina</button>
          </div>
        </div>
      )}
    >
      <PortalInner />
    </ErrorBoundary>
  );
}
